import { useMemo } from 'react'
import { BaseTable } from 'Features/Table/BaseTable.view'
import { ExtendedGridColDef } from 'Features/Table/BulkUpdateModal'
import {
  SheduleTableProps,
  SchedulerResourceAndMaterialConstraintsRowType,
} from './Scheduler.types'
import { Box } from '@mui/material'
import { basketPoolNonNegativeIntCellProps } from './SOR/SORTableColumns'

const dataConfig = {
  required: ['ResourceConstraint', 'ConstraintType', 'ConstraintAmount', 'UOM'],
  optional: ['openingFreeBaskets', 'fullBaskets'],
}
export const SchedulerResourceAndMaterialConstraintsTable = ({
  data,
  isLoading,
  updateTableData,
  uploadScheduleData,
  isUploading,
  isSaving,
  updateRowsStatus,
  predicate,
  deleteTableData,
  isDeleting,
  readOnly = false,
}: SheduleTableProps<SchedulerResourceAndMaterialConstraintsRowType>) => {
  // Handle data updates for BaseTable
  const handleDataUpdate = (
    changedRows: Record<number, SchedulerResourceAndMaterialConstraintsRowType>
  ) => {
    updateTableData(changedRows)
  }
  //Example data:
  //   {
  //     "ConstraintAmount": "2000",
  //     "ConstraintType": "RunRate",
  //     "ResourceConstraint": "sugar",
  //     "UOM": "KG",
  //     "id": 0
  // }
  const columns: ExtendedGridColDef[] = useMemo(() => {
    // Discover existing ConstraintType values from the loaded data so legacy
    // free-text values continue to render. `basket_pool` is always offered
    // (rows with this type are projected to BasketFamiliesDelta on save).
    const observed = new Set<string>()
    data.forEach((row) => {
      const v = (row as { ConstraintType?: unknown }).ConstraintType
      if (typeof v === 'string' && v.length > 0) observed.add(v)
    })
    const valueOptions = Array.from(
      new Set(['material', 'resource', 'basket_pool', ...observed])
    )
    return [
      {
        field: 'ResourceConstraint',
        headerName: 'Resource Constraint Name',
        flex: 1,
        type: 'string',
      },
      {
        field: 'ConstraintType',
        headerName: 'Constraint Type',
        flex: 1,
        editable: true,
        type: 'singleSelect',
        valueOptions,
      },
      {
        field: 'ConstraintAmount',
        headerName: 'Constraint Amount',
        type: 'number',
        flex: 1,
        editable: true,
      },
      {
        field: 'UOM',
        headerName: 'Unit Of Measure',
        flex: 1,
        editable: true,
      },
      // Basket-pool-only: hour-0 starting state. Persisted to
      // BasketFamiliesDelta by the API on save. See
      // Scheduler/docs/BASKET_TRACKING_HOURLY_LOGIC.md.
      {
        field: 'openingFreeBaskets',
        headerName: 'Opening Free Baskets',
        type: 'number',
        flex: 1,
        editable: true,
        preProcessEditCellProps: basketPoolNonNegativeIntCellProps,
      },
      {
        field: 'fullBaskets',
        headerName: 'Full Baskets',
        type: 'number',
        flex: 1,
        editable: true,
        preProcessEditCellProps: basketPoolNonNegativeIntCellProps,
      },
    ]
  }, [data])

  return (
    <Box>
      <BaseTable
        // Each user's saved view of this table (Save as my default).
        layoutKey="ResourceAndMaterialConstraints"
        data={data ?? []}
        columns={columns}
        isLoading={isLoading}
        updateTableData={handleDataUpdate}
        uploadData={uploadScheduleData}
        isUploading={isUploading}
        isSaving={isSaving}
        checkBoxSelection={true}
        showTableToolBar={true}
        downloadFileName="SchedulerResourceAndMaterialConstraints"
        onDeleteRows={deleteTableData}
        isDeleting={isDeleting}
        readOnly={readOnly}
        multiStageUpload={{
          uploadFile: uploadScheduleData,
          isUploading: isUploading,
          columns: columns,
          updateRowsStatus: updateRowsStatus,
          originalData: data,
          predicate: predicate,
          dataConfig: dataConfig,
        }}
        initialState={{
          pagination: { paginationModel: { pageSize: 100 } },
        }}
        sx={{
          maxWidth: 'calc(100vw - 70px)',
          '& .MuiDataGrid-cell--editable': {
            backgroundColor: '#CBD5E1',
          },
          '& .MuiBox-root': {
            padding: '0px !important',
          },
        }}
      />
    </Box>
  )
}
